import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { omit } from 'lodash';
import { Grid, Button, FormControl, Select, MenuItem, TextField, Tooltip } from '@material-ui/core';
import { ORDER_READONLY_FIELDS } from '@onward-delivery/core';
import { OnwardCard } from './Card';
import { useMutation } from '@apollo/client';
import { asDateInTZ } from '@/utilities/convertToISO';
import { getWarehouseManifestNumber } from '@/utilities/warehouseManifests';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { format } from 'date-fns';
import { UPSERT_ORDERS } from '@/components/ShipmentForm/graphql/mutations';
import { useFormatters } from '@/components/OrderDetailsPage/Crossdocking/hooks';
import { CardTitle, CardItem, CardSubtitle, CardItemBold } from './blocks';
import { PALLET_STATUSES, RECEIVED_STATUSES } from '@/constants/manifestStatuses';
import { Info } from '@material-ui/icons';
import { SingleDatePicker } from '../ShipmentForm/modals/InputFields';
import { useClientUser } from '@/hooks';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { Link } from 'react-router-dom';
import ItemSection from '../Manifest/cards/ItemSection';
import { PrimaryButton } from '@/styles/blocks';
import { INSERT_MANIFEST } from '../Manifests/graphql';
import { isUnallocatedForwardStocked } from '@/utilities/forwardStocking';
import ForwardStockingTag from '@/components/misc/ForwardStockingTag';
import { captureException } from '@sentry/react';
import { advance, patchEvent, receive } from '@onward-delivery/core';
import {
    INSERT_EMPTY_PALLET,
    INSERT_PALLET,
    UPDATE_ITEM_PALLETS_MANY,
    UPDATE_PALLET,
    UPDATE_PALLETS_MANY,
} from '../Manifest/graphql';
import StorePallets from '../Manifest/modals/StorePallets';
import PrintLabelsModal from '../Manifest/modals/PrintLabelsModal';
import { Header2 } from './Crossdocking/blocks';
import { enrichManifest } from '@/utilities/enrichManifest';
import { isOrderReceivedByPallet, singlePalletAtReceived } from '@/utilities/manifestPallets';
import { consolidatableItems } from '../Manifest/ManifestGrid/utils';
import { findMatchingWarehouseEvent, warehouseEstimateOrderFields } from '@/utilities/warehouseEvents';

const statusLabelMap = {
    RECEIVED: 'Received',
    NOT_DELIVERED: 'Not Received',
};

const buildReceiveUpsertEvents = (order, received_date) => {
    if ((order.wh_events || []).some((e) => e.action === 'START:PICKING_UP')) return [];

    let baseEvents = [];
    if (order.event_state === 'WAREHOUSE_RETURN') {
        const attemptEvent =
            [...(order.wh_events || [])].reverse().find((e) => e.transition?.next === 'WAREHOUSE_RETURN') || null;
        const attemptAction = attemptEvent?.action;

        baseEvents = patchEvent({
            curr: [],
            prev: order.wh_events || [],
            action: attemptAction,
            patch: { status: 'RECEIVED', received_date },
        });
    } else {
        baseEvents = receive(order, {}, { received_date }) || [];
    }

    return (baseEvents || []).filter(Boolean).map((event) => {
        const existing = findMatchingWarehouseEvent(order.wh_events, { action: event.action });
        const merged = existing?.event_id ? { ...existing, ...event } : event;
        const { __typename, state, transition, driver, wh_driver, location, ...remaining } = merged;
        return {
            ...remaining,
            order_id: remaining.order_id || order.order_id,
        };
    });
};

export default function WarehousingCard(props) {
    const {
        user_id,
        order,
        warehouse,
        editable,
        manifestEditable,
        warehouseEditable,
        refetchOrder,
        setNotification,
        orderNotes,
    } = props;

    const [openEditor, setOpenEditor] = useState(false);
    const [updates, setUpdates] = useState({});
    const formatters = useFormatters(order);
    const [submitOrder] = useMutation(UPSERT_ORDERS);
    const [hasItemsWithoutInboundManifest, setHasItemsWithoutInboundManifest] = useState(false);
    const [singleOrderManifest, setSingleOrderManifest] = useState(null);
    const [receivingDate, setReceivingDate] = useState();
    const [selectedWarehouse, setSelectedWarehouse] = useState();
    const { client_id, locations: warehouses } = useClientUser();
    const [storingPallets, storePalletsInit] = useState(null);
    const [printingLabels, printLabels] = useState();

    const orderTZ = useMemo(() => {
        return order.pickup_zip ? zipcode_to_timezone.lookup(order.pickup_zip) : 'America/New_York';
    }, [order]);

    const controllerAttr = useMemo(() => {
        return order.warehouse_control_entity === 'SHIPPER' ? 'carrier_id' : 'shipper_id';
    }, [order]);

    const clone = useMemo(() => {
        return {
            ...warehouse,
            est_ship_date: order?.warehouse_estimated_ship_date || warehouse?.est_ship_date || null,
            est_received_date: order?.warehouse_estimated_delivery_date || warehouse?.est_received_date || null,
            ...updates,
        };
    }, [order, warehouse, updates]);

    const statusLabel = useMemo(() => {
        if (clone?.status === 'RECEIVED') {
            return (
                <CardItemBold
                    className="my-1"
                    css={css`
                        color: ${colors.greens.primary};
                    `}
                >
                    {statusLabelMap[clone.status]}
                </CardItemBold>
            );
        }

        let [received, total] = (order?.itemsByOrderId || []).reduce(
            ([recAcc, totAcc], item) => {
                const qty = item.quantity && item.quantity >= 1 ? item.quantity : 1;
                return [recAcc + (RECEIVED_STATUSES.includes(item.pallet?.warehouse_status) ? qty : 0), totAcc + qty];
            },
            [0, 0]
        );

        received = Number(received).toFixed(0);
        total = Number(total).toFixed(0);

        if (Number(received) > 0) {
            return (
                <CardItemBold
                    className="my-1"
                    css={css`
                        color: ${colors.blues[0]};
                    `}
                >
                    {received}/{total} Received
                </CardItemBold>
            );
        }

        return (
            <CardItemBold
                className="my-1"
                css={css`
                    color: ${colors.greys[3]};
                `}
            >
                Not Received
            </CardItemBold>
        );
    }, [clone, order]);

    const manifestNumberDisplay = useMemo(() => getWarehouseManifestNumber(order), [order]);

    const inboundManifests = useMemo(() => {
        const _manifests = {};
        (order?.itemsByOrderId || []).forEach((item) => {
            if (item.manifests?.length) {
                item.manifests.forEach((manifestMapping) => {
                    if (
                        manifestMapping.manifest &&
                        ['WILL_CALL', 'INBOUND', 'CROSS_DOCK'].includes(manifestMapping.manifest.type)
                    ) {
                        const manifestId = manifestMapping.manifest.manifest_id;
                        if (!_manifests[manifestId]) {
                            _manifests[manifestId] = {
                                ...manifestMapping.manifest,
                                items: [],
                            };
                        }
                        const { manifest, ...rest } = manifestMapping;
                        _manifests[manifestId].items.push({
                            ...rest,
                            item: { ...item, order: order },
                        });
                    } else {
                        setHasItemsWithoutInboundManifest(true);
                    }
                });
            } else {
                setHasItemsWithoutInboundManifest(true);
            }
        });
        return Object.values(_manifests);
    }, [order]);

    useEffect(() => {
        if (inboundManifests.length === 1 && !hasItemsWithoutInboundManifest) {
            setSingleOrderManifest(enrichManifest(inboundManifests[0]));
        } else {
            setSingleOrderManifest(null);
        }
    }, [inboundManifests, hasItemsWithoutInboundManifest]);

    const canCreateSingleOrderManifest = useMemo(() => {
        if (isUnallocatedForwardStocked(order)) {
            return false;
        }

        if (order?.wh_events) {
            if (order.wh_events.some((event) => event.action && event.action.includes(':ADD_CD'))) {
                return false;
            }
        }

        if ((order.itemsByOrderId || []).some((item) => item?.exceptions?.length > 0)) {
            return false;
        }

        if (inboundManifests?.length > 0) {
            return false;
        }

        return true;
    }, [order, inboundManifests]);

    const canPreConsolidate = useMemo(() => {
        const noItemsWithPallets = singleOrderManifest?.items?.every((item) => !item.item.pallet_id);
        return singleOrderManifest?.type === 'INBOUND' && noItemsWithPallets;
    }, [singleOrderManifest]);

    const [insertManifest, { loading: createLoading }] = useMutation(INSERT_MANIFEST, {
        onError: (error) => {
            console.log(error.message);
            captureException(error);
        },
        onCompleted: ({ created }) => {
            refetchOrder();
        },
    });

    const createSingleOrderManifest = () => {
        const itemIds = order.itemsByOrderId.map((item) => ({ item_id: item.item_id }));

        insertManifest({
            variables: {
                manifest: {
                    client_id,
                    type: 'INBOUND',
                    source: 'MANUAL',
                    items: {
                        data: itemIds,
                    },
                    warehouse_id: selectedWarehouse,
                    receiving_date: receivingDate,
                },
            },
        });
    };

    const singleManifestItemsDisplay = useMemo(() => {
        const itemsByPalletId = (order.itemsByOrderId || []).reduce((acc, item, i) => {
            const palletId = item.pallet_id || `no_pallet_${i}`;
            if (!acc[palletId]) {
                acc[palletId] = [];
            }
            acc[palletId].push(item);
            return acc;
        }, {});

        const sortedItems = Object.values(itemsByPalletId).flatMap((items) => {
            return items.map((item, index) => ({
                ...item,
                isFirstOnPallet: index === 0,
                isLastOnPallet: index === items.length - 1,
            }));
        });

        return sortedItems;
    }, [order]);

    // Units that can't be received stay off the single label: receiving that pallet would receive them too.
    const singleLabelItems = useMemo(
        () => consolidatableItems(singleManifestItemsDisplay),
        [singleManifestItemsDisplay]
    );

    const [insertPallet, { loading: insertPalletLoading }] = useMutation(INSERT_PALLET, {
        onError: (error) => {
            console.log(error.message);
            captureException(error);
        },
    });

    const [insertEmptyPallet, { loading: insertEmptyPalletLoading }] = useMutation(INSERT_EMPTY_PALLET, {
        onError: (error) => {
            console.log(error.message);
            captureException(error);
        },
    });

    const [updatePallets, { loading: updatePalletsLoading }] = useMutation(UPDATE_PALLETS_MANY, {
        onError: (error) => {
            console.log(error.message);
            captureException(error);
        },
    });

    const [updatePallet, { loading: updatePalletLoading }] = useMutation(UPDATE_PALLET, {
        onError: (error) => {
            console.log(error.message);
            captureException(error);
        },
    });

    const [updateItemPallets, { loading: updateItemPalletsLoading }] = useMutation(UPDATE_ITEM_PALLETS_MANY, {
        onError: (error) => {
            console.log(error.message);
            captureException(error);
        },
    });

    const preconsolidateItems = useCallback((items, manifest) => {
        insertEmptyPallet({
            variables: {
                object: {
                    type: 'CONSOLIDATED',
                    warehouse_id: manifest.warehouse_id,
                    warehouse_location: null,
                    warehouse_status: 'NOT_RECEIVED',
                    pallet_name: `${order.order_number} consolidated label`,
                },
            },
            onCompleted: ({ insert_pallet_items_one }) => {
                const itemUpdates = items.map((item) => ({
                    where: { item_id: { _eq: item.item_id } },
                    _set: { pallet_id: insert_pallet_items_one.pallet_id },
                }));

                updateItemPallets({
                    variables: {
                        itemUpdates: itemUpdates,
                        palletUpdates: [],
                        palletsToDelete: [],
                    },
                });
            },
        });
    }, []);

    const receiveItem = useCallback(
        (item, manifest) => {
            let events;
            if (
                manifest.items.every(
                    (itemMapping) =>
                        itemMapping.item?.item_id === item.item_id ||
                        RECEIVED_STATUSES.includes(itemMapping.item?.pallet?.warehouse_status)
                )
            ) {
                events = buildReceiveUpsertEvents(order, new Date().toISOString());
            }

            insertPallet({
                variables: {
                    item_id: item.item_id,
                    pallet: singlePalletAtReceived(manifest.warehouse_id),
                    ...(events?.length ? { events } : {}),
                },
                onCompleted: () => {
                    refetchOrder();
                },
            });
        },
        [order]
    );

    const receivePallet = useCallback(
        (item, pallet, manifest) => {
            let events;
            // Only this order's event is written here: an order-detail page has no visibility into
            // other orders sharing the pallet. Receive from the Manifest screen to advance them all.
            if (isOrderReceivedByPallet(order, pallet.pallet_id)) {
                events = buildReceiveUpsertEvents(order, new Date().toISOString());
            }

            updatePallet({
                variables: {
                    pallet_id: pallet.pallet_id,
                    update: {
                        warehouse_id: manifest.warehouse_id,
                        warehouse_location: null,
                        warehouse_status: PALLET_STATUSES.RECEIVED,
                    },
                    ...(events?.length ? { events } : {}),
                },
            });
        },
        [order]
    );

    const storePallets = useCallback((pallets) => {
        return updatePallets({
            variables: {
                updates: pallets.map((pallet) => ({
                    where: { pallet_id: { _eq: pallet.pallet_id } },
                    _set: {
                        warehouse_location: pallet.warehouse_location,
                        warehouse_status: PALLET_STATUSES.STORED,
                        is_pool: pallet.is_pool,
                    },
                })),
            },
            onError: (error) => {
                if (error.message.includes('pallet_locations_unique')) {
                    setNotification({ severity: 'error', message: 'Bin location is already occupied' });
                } else {
                    setNotification(
                        `Failed to store ${pallets.some((pallet) => pallet.type === 'SINGLE') ? 'item' : 'pallet'}`
                    );
                }
            },
        });
    }, []);

    const handleSave = useCallback(() => {
        const { itemsByOrderId, ...remaining } = order;
        const isReceived = clone.status === 'RECEIVED' && warehouse.status !== 'RECEIVED';
        let events = advance(order, order.event_state, isReceived ? 'RECEIVING' : 'AWAITING');
        events = patchEvent(order.wh_events, {
            curr: events,
            prev: order.wh_events,
            action: 'START:AWAITING',
            notes: clone.notes,
            est_received_date: clone.est_received_date,
            est_ship_date: clone.est_ship_date,
        });

        submitOrder({
            variables: {
                orders: [
                    {
                        ...omit(remaining, ORDER_READONLY_FIELDS),
                        ...warehouseEstimateOrderFields(updates),
                        wh_events: {
                            data: events
                                .filter((event) => Object.keys(event).length > 0)
                                .map((event) => {
                                    const { state, transition, ...remaining } = event;
                                    return remaining;
                                }),
                        },
                    },
                ],
                items: [],
                removals: [],
            },
        }).then(() => {
            setUpdates({});
            setOpenEditor(false);
        });
    }, [order, clone, warehouse, updates]);

    const CardRow = ({ children, ...rest }) => {
        return (
            <Grid
                container
                item
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                style={{ marginTop: '0.75rem', marginBottom: '0.75rem' }}
                {...rest}
            >
                {children}
            </Grid>
        );
    };

    return (
        <OnwardCard>
            <div
                css={css`
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 1.5rem;
                `}
            >
                <Header2 style={{ display: 'block', marginBottom: 0 }}>Warehousing</Header2>
                <ForwardStockingTag order={order} />
            </div>
            <CardRow>
                <Grid item>
                    <CardSubtitle>Status</CardSubtitle>
                </Grid>
                <Grid item>
                    {openEditor ? (
                        <FormControl variant="outlined" size="small">
                            <Select
                                value={clone.status}
                                name="raw-select"
                                disabled={warehouse.status === 'RECEIVED'}
                                onChange={(e) => setUpdates((prev) => ({ ...prev, status: e.target.value }))}
                                MenuProps={{
                                    disableScrollLock: true,
                                    anchorOrigin: {
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    },
                                    transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'left',
                                    },
                                    getContentAnchorEl: null,
                                }}
                            >
                                {Object.keys(statusLabelMap).map((status) => (
                                    <MenuItem value={status}>{statusLabelMap[status]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <>{statusLabel}</>
                    )}
                </Grid>
            </CardRow>
            <CardRow>
                <Grid item>
                    <CardItem>Estimated Ship Date</CardItem>
                </Grid>
                <Grid item>
                    {openEditor ? (
                        <TextField
                            type="date"
                            variant="outlined"
                            label="Est. Ship Date"
                            InputLabelProps={{ shrink: true }}
                            value={clone.est_ship_date ? format(new Date(clone.est_ship_date), 'yyyy-MM-dd') : ''}
                            onChange={(e) => {
                                let dateStr = null;
                                if (e.target.value) {
                                    dateStr = asDateInTZ(e.target.value, orderTZ).toISOString();
                                    setUpdates((prev) => ({ ...prev, est_ship_date: dateStr }));
                                }
                            }}
                        />
                    ) : (
                        <CardItemBold className="my-1">
                            {clone.est_ship_date ? formatters.dateShort.format(new Date(clone.est_ship_date)) : 'N/A'}
                        </CardItemBold>
                    )}
                </Grid>
            </CardRow>
            <CardRow>
                <Grid item>
                    <CardItem>Estimated Received Date</CardItem>
                </Grid>
                <Grid item>
                    {openEditor ? (
                        <TextField
                            type="date"
                            variant="outlined"
                            label="Est. Received Date"
                            InputLabelProps={{ shrink: true }}
                            value={
                                clone.est_received_date ? format(new Date(clone.est_received_date), 'yyyy-MM-dd') : ''
                            }
                            onChange={(e) => {
                                let dateStr = null;
                                if (e.target.value) {
                                    dateStr = asDateInTZ(e.target.value, orderTZ).toISOString();
                                    setUpdates((prev) => ({ ...prev, est_received_date: dateStr }));
                                }
                            }}
                        />
                    ) : (
                        <CardItemBold className="my-1">
                            {clone.est_received_date
                                ? formatters.dateShort.format(new Date(clone.est_received_date))
                                : 'N/A'}
                        </CardItemBold>
                    )}
                </Grid>
            </CardRow>
            <CardRow>
                <Grid item>
                    <CardItem>Actual Received Date</CardItem>
                </Grid>
                <Grid item>
                    {openEditor && clone.received_date ? (
                        <TextField
                            type="datetime-local"
                            variant="outlined"
                            label="Est. Received Date"
                            InputLabelProps={{ shrink: true }}
                            value={
                                // datetime-local requires yyyy-MM-ddTHH:mm; orderTZ keeps this symmetric with the onChange write
                                clone.received_date
                                    ? formatInTimeZone(new Date(clone.received_date), orderTZ, "yyyy-MM-dd'T'HH:mm")
                                    : ''
                            }
                            onChange={(e) => {
                                let dateStr = null;
                                if (e.target.value) {
                                    // asDateInTZ mis-parses date-time strings as browser-local; zonedTimeToUtc handles the time part
                                    dateStr = zonedTimeToUtc(e.target.value, orderTZ).toISOString();
                                    setUpdates((prev) => ({ ...prev, received_date: dateStr }));
                                }
                            }}
                        />
                    ) : (
                        <>
                            <CardItemBold className="my-1">
                                {clone.received_date
                                    ? formatters.dateShort.format(new Date(clone.received_date))
                                    : 'Pending'}
                            </CardItemBold>
                        </>
                    )}
                </Grid>
            </CardRow>
            <CardRow>
                <Grid item>
                    <CardItem>Actual Received Time</CardItem>
                </Grid>
                <Grid item>
                    {openEditor && clone.received_date ? (
                        <>
                            <CardItemBold className="my-1">Choose above</CardItemBold>
                        </>
                    ) : (
                        <>
                            <CardItemBold className="my-1">
                                {clone.received_date
                                    ? formatters.timeShort.format(new Date(clone.received_date))
                                    : 'Pending'}
                            </CardItemBold>
                        </>
                    )}
                </Grid>
            </CardRow>
            <CardRow item>
                <Grid item>
                    <CardItem>Manifest Number</CardItem>
                </Grid>
                <Grid item>
                    <CardItemBold className="my-1">{manifestNumberDisplay || '--'}</CardItemBold>
                </Grid>
            </CardRow>
            <CardRow item>
                <Grid item>
                    <CardItem>Trailer ID</CardItem>
                </Grid>
                <Grid item>
                    <CardItemBold className="my-1">{order.warehouse_trailer_id || '--'}</CardItemBold>
                </Grid>
            </CardRow>
            <CardRow item>
                <CardSubtitle style={{ marginTop: '1.5rem' }}>Notes</CardSubtitle>
            </CardRow>
            {!!orderNotes?.warehouse?.length && (
                <CardRow item>
                    <CardItem>{(orderNotes?.warehouse || []).map((n) => n.note).join(' / ')}</CardItem>
                </CardRow>
            )}
            <CardRow item>
                {openEditor ? (
                    <TextField
                        variant="outlined"
                        className="w-100"
                        multiline
                        rows={3}
                        value={clone.notes}
                        onChange={(e) => setUpdates((prev) => ({ ...prev, notes: e.target.value }))}
                    />
                ) : (
                    <CardItem>{clone.notes ? clone.notes : 'Add additional notes here'}</CardItem>
                )}
            </CardRow>
            <CardRow style={{ marginTop: '1rem', marginBottom: 0 }}>
                {openEditor ? (
                    <Grid container spacing={2} item sm={12} className="d-flex justify-content-end">
                        <Grid item>
                            <Button
                                onClick={() => {
                                    setUpdates({});
                                    setOpenEditor(false);
                                }}
                                variant="contained"
                                color="white"
                            >
                                <CardItemBold>Cancel</CardItemBold>
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button onClick={handleSave} variant="contained" color="primary">
                                <CardItemBold>Save</CardItemBold>
                            </Button>
                        </Grid>
                    </Grid>
                ) : (
                    <>
                        {warehouseEditable ? (
                            <Grid item sm={12} className="d-flex flex-column align-items-end">
                                <Button
                                    onClick={() => setOpenEditor(true)}
                                    variant="contained"
                                    color="primary"
                                    className="updateTime-btn"
                                >
                                    <CardItemBold>Update Status</CardItemBold>
                                </Button>
                            </Grid>
                        ) : null}
                    </>
                )}
            </CardRow>
            {manifestEditable && (
                <>
                    <CardRow item>
                        <CardSubtitle style={{ marginTop: '1.5rem' }}>Manifests</CardSubtitle>
                    </CardRow>
                    <CardRow>
                        {singleOrderManifest ? (
                            <>
                                <CardRow>
                                    <CardSubtitle>Inbound Manifest {singleOrderManifest.manifest_number}</CardSubtitle>
                                </CardRow>
                                <CardRow
                                    css={css`
                                        justify-content: flex-start;
                                    `}
                                >
                                    <PrimaryButton onClick={() => printLabels(singleOrderManifest)}>
                                        Print Labels
                                    </PrimaryButton>
                                </CardRow>
                                {canPreConsolidate && singleLabelItems.length > 1 && (
                                    <CardRow
                                        css={css`
                                            justify-content: flex-start;
                                        `}
                                    >
                                        <PrimaryButton
                                            onClick={() => preconsolidateItems(singleLabelItems, singleOrderManifest)}
                                        >
                                            Use Single Label
                                        </PrimaryButton>
                                        <Tooltip
                                            style={{ color: '#59B863', marginLeft: '5px' }}
                                            title={
                                                'Click to consolidate items together onto a single order-level pallet. You can then use this single pallet label for scanning across all warehouse management actions.'
                                            }
                                            placement="top"
                                        >
                                            <Info />
                                        </Tooltip>
                                    </CardRow>
                                )}
                                <CardRow>
                                    <CardItem>
                                        To access all warehousing features for this manifest, click{' '}
                                        <Link
                                            to={`/manifests/${singleOrderManifest.manifest_id}`}
                                            css={css`
                                                color: #59b863;
                                                font-weight: 500;
                                            `}
                                        >
                                            Here
                                        </Link>
                                    </CardItem>
                                </CardRow>
                                {singleManifestItemsDisplay.map((item) => (
                                    <ItemSection
                                        key={item.item_id}
                                        manifest={singleOrderManifest}
                                        item={item}
                                        order={order}
                                        options={{ editOrderModal: true }}
                                        callbacks={{
                                            receiveItem: receiveItem,
                                            receivePallet: receivePallet,
                                            storePalletsInit: storePalletsInit,
                                        }}
                                        editable={manifestEditable}
                                    />
                                ))}
                            </>
                        ) : inboundManifests.length === 0 ? (
                            <>
                                <CardRow>
                                    <SingleDatePicker
                                        label="Receiving Date"
                                        value={
                                            receivingDate
                                                ? formatInTimeZone(new Date(receivingDate), 'UTC', 'yyyy-MM-dd')
                                                : null
                                        }
                                        onChange={(e) => {
                                            setReceivingDate(asDateInTZ(e.target.value, 'UTC').toISOString());
                                        }}
                                    />
                                </CardRow>
                                <CardRow>
                                    <TextField
                                        variant="outlined"
                                        select
                                        fullWidth
                                        label="Warehouse"
                                        name="warehouse_id"
                                        InputLabelProps={{ shrink: true }}
                                        value={selectedWarehouse || ''}
                                        onChange={(e) => setSelectedWarehouse(e.target.value)}
                                        css={css`
                                            margin-top: 1rem;
                                            margin-right: 8px;
                                        `}
                                    >
                                        {warehouses.map((warehouse) => (
                                            <MenuItem key={warehouse.location_id} value={warehouse.location_id}>
                                                {warehouse.location_name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </CardRow>

                                <CardRow
                                    css={css`
                                        justify-content: flex-start;
                                    `}
                                >
                                    <PrimaryButton
                                        onClick={() => createSingleOrderManifest()}
                                        disabled={!canCreateSingleOrderManifest || !receivingDate || !selectedWarehouse}
                                    >
                                        Receive as single order
                                    </PrimaryButton>
                                    <Tooltip
                                        style={{ color: '#59B863', marginLeft: '5px' }}
                                        title={`Select a receiving date and warehouse, then press to create a single inbound manifest for the whole order. ${
                                            canCreateSingleOrderManifest
                                                ? ''
                                                : 'Not available for cross-docked orders, forward-stocked orders awaiting allocation, or if some items are already in an inbound manifest or have exceptions. See the Warehousing section for more options.'
                                        }`}
                                        placement="right"
                                    >
                                        <Info />
                                    </Tooltip>
                                </CardRow>
                            </>
                        ) : (
                            <CardRow>
                                <CardItem>
                                    This order has items on the following inbound manifest(s). Follow the links for full
                                    warehousing features:{' '}
                                    {inboundManifests.map((manifest, i) => (
                                        <React.Fragment key={i}>
                                            {i > 0 && ', '}
                                            <Link
                                                to={`/manifests/${manifest.manifest_id}`}
                                                css={css`
                                                    color: #59b863;
                                                    font-weight: 500;
                                                `}
                                            >
                                                Manifest {manifest.manifest_number}
                                            </Link>
                                        </React.Fragment>
                                    ))}
                                </CardItem>
                            </CardRow>
                        )}
                    </CardRow>
                </>
            )}
            <StorePallets
                pallets={storingPallets}
                warehouseId={singleOrderManifest?.warehouse_id}
                callbacks={{ storePalletsInit: storePalletsInit, storePallets: storePallets }}
                loading={{ palletsLoading: updatePalletsLoading || updatePalletLoading }}
            />
            <PrintLabelsModal
                printingManifest={printingLabels}
                loading={{
                    updateLoading: updatePalletsLoading || updatePalletLoading || insertPalletLoading || createLoading,
                }}
                callbacks={{ printLabels: printLabels }}
            />
        </OnwardCard>
    );
}
